/**
 * 全部 Listing — 管理员/组长查看所有用户的 Listing
 */

import { authFetch } from './api.js';
import { getStoredUser } from '../interaction/transport/http-client.js';
import { showToast, formatDate, formatNumber, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { openImageManager } from './image-manager.js';
import { openVideoManager } from './video-manager.js';
import { loadChartLibrary } from '../interaction/ui/chart-loader.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';
import { getPeriodDateRange, applyDateInputs, readDateInputs } from './dashboard-context.js';
import { bindCostDetailPopovers, createCostBadge } from './cost-detail-popover.js';
import { getUserRole } from './state.js';

let currentPage = 1;
let currentSearch = '';

function getCurrentUserId() {
  return getStoredUser()?.id || null;
}
let ownershipChart = null;
let trendChart = null;
let isOverviewMode = false;
const ALL_LISTINGS_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  search: { type: 'string', defaultValue: '' },
};

export function initAllListings() {
  hydrateAllListingsFiltersFromUrl();
  registerViewLoader('admin-all-listings', () => loadAllListings());
  bindAllListingsTabs();
  bindAllListingsOverviewFilters();

  const searchBtn = document.getElementById('all-listings-search-btn');
  if (searchBtn) searchBtn.addEventListener('click', searchAllListings);

  const searchInput = document.getElementById('all-listings-search');
  if (searchInput) {
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') searchAllListings();
    });
  }
}

function hydrateAllListingsFiltersFromUrl() {
  const state = readScopedQuery('alllistings', ALL_LISTINGS_QUERY_SCHEMA);
  const searchInput = document.getElementById('all-listings-search');
  currentSearch = state.search || '';
  if (searchInput) {
    searchInput.value = currentSearch;
  }
}

async function loadAllListings(page = null) {
  if (isOverviewMode) {
    await loadAllListingsOverview();
    return;
  }
  const state = readScopedQuery('alllistings', ALL_LISTINGS_QUERY_SCHEMA);
  currentPage = page == null ? state.page : page;
  currentSearch = state.search || '';
  const searchInput = document.getElementById('all-listings-search');
  if (searchInput && searchInput.value.trim() !== currentSearch) {
    searchInput.value = currentSearch;
  }
  writeScopedQuery('alllistings', { page: currentPage, search: currentSearch });
  const tbody = document.getElementById('all-listings-tbody');

  const data = await withTableLoading({
    tbody,
    colSpan: 6,
    page: currentPage,
    errorMessage: '加载 Listing 列表失败',
    onRetry: (p) => loadAllListings(p),
    onError: (error) => {
      console.error('[AllListings] Error loading listings:', error);
      showToast('加载 Listing 列表失败', 'error');
    },
    task: async () => {
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
    if (currentSearch) params.set('search', currentSearch);

    const resp = await authFetch(`/api/admin/listings?${params}`);
    if (!resp.ok) throw new Error('Failed to load listings');
      return resp.json();
    },
  });
  if (!data) return;
  renderAllListingsTable(data.listings || []);
  renderPagination('all-listings-pagination', data.total, currentPage, 20, (p) => loadAllListings(p));
  renderAllListingsPageMeta(data.total, currentPage, 20);
}

function bindAllListingsTabs() {
  const tabList = document.getElementById('all-listings-tab-list');
  const tabOverview = document.getElementById('all-listings-tab-overview');
  if (tabList) {
    tabList.addEventListener('click', () => switchAllListingsTab(false));
  }
  if (tabOverview) {
    tabOverview.addEventListener('click', () => switchAllListingsTab(true));
  }
}

function bindAllListingsOverviewFilters() {
  const periodToggle = document.getElementById('all-listings-overview-period-toggle');
  const defaultRange = getPeriodDateRange('month');
  applyDateInputs('all-listings-overview-start', 'all-listings-overview-end', defaultRange);
  periodToggle?.querySelectorAll('button').forEach((btn) => {
    btn.addEventListener('click', () => {
      periodToggle.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      const range = getPeriodDateRange(btn.dataset.period || 'month');
      applyDateInputs('all-listings-overview-start', 'all-listings-overview-end', range);
      periodToggle.dataset.custom = '0';
      if (isOverviewMode) loadAllListingsOverview();
    });
  });

  document.getElementById('all-listings-overview-apply')?.addEventListener('click', () => {
    const range = readDateInputs('all-listings-overview-start', 'all-listings-overview-end', null);
    if (!range?.startDate || !range?.endDate) {
      showToast('请选择开始和结束日期', 'error');
      return;
    }
    if (range.startDate > range.endDate) {
      showToast('开始日期不能晚于结束日期', 'error');
      return;
    }
    if (periodToggle) {
      periodToggle.dataset.custom = '1';
      periodToggle.querySelectorAll('button').forEach((b) => b.classList.remove('active'));
    }
    if (isOverviewMode) loadAllListingsOverview();
  });
}

function switchAllListingsTab(showOverview) {
  isOverviewMode = showOverview;
  const tabList = document.getElementById('all-listings-tab-list');
  const tabOverview = document.getElementById('all-listings-tab-overview');
  const listPanel = document.getElementById('all-listings-list-panel');
  const overviewPanel = document.getElementById('all-listings-overview-panel');
  if (tabList) tabList.classList.toggle('active', !showOverview);
  if (tabOverview) tabOverview.classList.toggle('active', showOverview);
  if (listPanel) listPanel.hidden = showOverview;
  if (overviewPanel) overviewPanel.hidden = !showOverview;
  if (showOverview) {
    loadAllListingsOverview();
    return;
  }
  destroyOverviewCharts();
  loadAllListings(currentPage || 1);
}

async function loadAllListingsOverview() {
  try {
    const periodToggle = document.getElementById('all-listings-overview-period-toggle');
    const activePeriod = periodToggle?.querySelector('button.active')?.dataset.period || 'month';
    const customMode = periodToggle?.dataset.custom === '1';
    const localRange = readDateInputs('all-listings-overview-start', 'all-listings-overview-end', getPeriodDateRange(activePeriod));
    const params = new URLSearchParams({
      period: customMode ? 'custom' : activePeriod,
    });
    if (customMode && localRange?.startDate && localRange?.endDate) {
      params.set('startDate', localRange.startDate);
      params.set('endDate', localRange.endDate);
    }
    const groupId = document.getElementById('dashboard-group-filter')?.value || 'all';
    if (groupId && groupId !== 'all') params.set('groupId', groupId);
    const resp = await authFetch(`/api/admin/listings/overview?${params}`);
    if (!resp.ok) throw new Error('Failed to load listing overview');
    const data = await resp.json();
    applyDateInputs('all-listings-overview-start', 'all-listings-overview-end', {
      startDate: data.startDate,
      endDate: data.endDate,
    });
    renderOverviewSummary(data.summary || {});
    const chartLibrary = (data.ownership?.length || data.trend?.length) ? await loadChartLibrary() : null;
    renderOwnershipChart(data.ownership || [], chartLibrary?.Chart);
    renderTrendChart(data.trend || [], chartLibrary?.Chart);
  } catch (error) {
    console.error('[AllListings] Error loading overview:', error);
    showToast('加载 Listing 统计图表失败', 'error');
  }
}

function renderOverviewSummary(summary) {
  const el = document.getElementById('all-listings-overview-summary-grid');
  if (!el) return;
  const cards = [
    { icon: '📄', label: 'Listing 数量', value: summary.listingsTotal || 0 },
    { icon: '🖼️', label: '图片数量', value: summary.imagesTotal || 0 },
    { icon: '🧮', label: 'Token 消耗', value: summary.tokensTotal || 0 },
    { icon: '🔌', label: 'MCP 调用', value: summary.mcpCallsTotal || 0 },
  ];
  el.replaceChildren(...cards.map((card) => {
    const element = createAllListingsElement('div', 'stat-card');
    element.append(
      createAllListingsElement('div', 'stat-card-icon', card.icon),
      createAllListingsElement('div', 'stat-card-label', card.label),
      createAllListingsElement('div', 'stat-card-value', formatNumber(card.value)),
    );
    return element;
  }));
}

function renderOwnershipChart(ownership, Chart) {
  const canvas = document.getElementById('all-listings-ownership-chart');
  if (!canvas || !Chart) return;
  ownershipChart?.destroy?.();
  const safeData = ownership.slice(0, 8);
  if (safeData.length === 0) return;
  const labels = safeData.map((x) => x.userName);
  const values = safeData.map((x) => x.listingCount || 0);
  ownershipChart = new Chart(canvas.getContext('2d'), {
    type: 'pie',
    data: {
      labels,
      datasets: [{
        data: values,
        backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#22d3ee', '#8b5cf6', '#84cc16', '#f97316'],
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { color: '#a1a1aa' } },
      },
    },
  });
}

function renderTrendChart(trend, Chart) {
  const canvas = document.getElementById('all-listings-trend-chart');
  if (!canvas || !Chart) return;
  trendChart?.destroy?.();
  const labels = trend.map((x) => formatDate(x.date, 'short'));
  const values = trend.map((x) => x.listings || 0);
  trendChart = new Chart(canvas.getContext('2d'), {
    type: 'line',
    data: {
      labels,
      datasets: [{
        label: 'Listing 数量',
        data: values,
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59,130,246,0.2)',
        fill: true,
        tension: 0.25,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
      scales: {
        y: { beginAtZero: true, ticks: { color: '#a1a1aa' }, grid: { color: 'rgba(113,113,122,0.15)' } },
        x: { ticks: { color: '#a1a1aa' }, grid: { display: false } },
      },
    },
  });
}

function destroyOverviewCharts() {
  ownershipChart?.destroy?.();
  trendChart?.destroy?.();
  ownershipChart = null;
  trendChart = null;
}

window.destroyAllListingsCharts = destroyOverviewCharts;

function createAllListingsElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createAllListingsActionButton(action, listingId, text, className = 'btn btn-secondary btn-sm') {
  const button = createAllListingsElement('button', className, text);
  button.type = 'button';
  button.dataset.action = action;
  button.dataset.listingId = String(listingId ?? '');
  return button;
}

function createAllListingsEmptyRow() {
  const state = createAllListingsElement('div', 'empty-state');
  state.append(
    createAllListingsElement('div', 'empty-icon', '📄'),
    createAllListingsElement('p', '', '暂无 Listing 数据'),
  );
  const cell = document.createElement('td');
  cell.colSpan = 6;
  cell.appendChild(state);
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

function createAllListingsRow(listing) {
  const name = listing.name || 'Untitled';
  const asin = listing.asin || 'N/A';
  const marketplace = listing.marketplace || '—';
  const userName = listing.user_name || listing.user_email || '-';
  const title = listing.title || listing.title_en || '暂无标题';
  const row = document.createElement('tr');

  const identityCell = document.createElement('td');
  const identity = createAllListingsElement('div', 'all-listings-title-cell');
  identity.append(
    createAllListingsElement('div', 'all-listings-asin', asin),
    createAllListingsElement('div', 'all-listings-name', name),
  );
  identityCell.appendChild(identity);
  row.appendChild(identityCell);

  const titleCell = document.createElement('td');
  const titleElement = createAllListingsElement('div', 'all-listings-main-title', title);
  titleElement.title = String(title);
  titleCell.appendChild(titleElement);
  row.appendChild(titleCell);

  const ownerCell = document.createElement('td');
  ownerCell.append(
    createAllListingsElement('div', 'all-listings-user', userName),
    createAllListingsElement('div', 'all-listings-market', `站点 ${marketplace}`),
  );
  row.appendChild(ownerCell);

  const costCell = document.createElement('td');
  costCell.appendChild(createCostBadge(listing.cost));
  row.appendChild(costCell);
  row.appendChild(createAllListingsElement('td', 'mono', formatDate(listing.created_at || listing.createdAt)));

  const actionCell = document.createElement('td');
  const actions = createAllListingsElement('div', 'all-listings-actions');
  const restore = createAllListingsActionButton('admin-restore-listing', listing.id, '工作台预览');
  restore.title = '在工作台打开此 Listing（他人内容为只读预览）';
  actions.append(
    createAllListingsActionButton('admin-view-images', listing.id, '查看图片'),
    createAllListingsActionButton('admin-view-videos', listing.id, '查看视频'),
    restore,
  );
  const canDelete = listing.user_id === getCurrentUserId() || getUserRole() === 'admin';
  if (canDelete) {
    actions.appendChild(createAllListingsActionButton('admin-delete-listing', listing.id, '删除', 'btn btn-danger btn-sm'));
  }
  actionCell.appendChild(actions);
  row.appendChild(actionCell);
  return row;
}

function bindAllListingsActions(root) {
  root.querySelectorAll('button[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const listingId = btn.dataset.listingId;
      const action = btn.dataset.action;
      if (action === 'admin-view-images') viewAdminListingImages(listingId);
      if (action === 'admin-view-videos') viewAdminListingVideos(listingId);
      if (action === 'admin-restore-listing') restoreListing(listingId);
      if (action === 'admin-delete-listing') deleteListing(listingId);
    });
  });
}

function renderAllListingsTable(listings) {
  const tbody = document.getElementById('all-listings-tbody');
  if (!tbody) return;

  if (!listings || listings.length === 0) {
    tbody.replaceChildren(createAllListingsEmptyRow());
    return;
  }

  tbody.replaceChildren(...listings.map((listing) => createAllListingsRow(listing)));
  bindAllListingsActions(tbody);
  bindCostDetailPopovers(tbody);
}

function renderAllListingsPageMeta(total, page, size) {
  const el = document.getElementById('all-listings-page-meta');
  if (!el) return;
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / size));
  el.textContent = `第 ${page} 页 / 共 ${totalPages} 页`;
}

function searchAllListings() {
  const input = document.getElementById('all-listings-search');
  currentSearch = input ? input.value.trim() : '';
  writeScopedQuery('alllistings', { page: 1, search: currentSearch });
  loadAllListings(1);
}

function restoreListing(listingId) {
  window.location.href = `/listing.html?restoreId=${listingId}&step=3`;
}

async function deleteListing(listingId) {
  if (!confirm('确定要删除这个 Listing 吗？')) return;

  try {
    const response = await authFetch(`/api/admin/listings/${listingId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete listing');

    showToast('Listing 已归档', 'success');
    await loadAllListings(currentPage);
  } catch (error) {
    console.error('[AllListings] Error deleting listing:', error);
    showToast('删除失败', 'error');
  }
}

async function viewAdminListingImages(listingId) {
  openImageManager(listingId, 'admin-all-listings');
}

async function viewAdminListingVideos(listingId) {
  openVideoManager(listingId, 'admin-all-listings');
}
